import { Icon, V2Badge, V2Card, V2CardHeader } from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';

interface SetupSection {
  readonly isReady: boolean;
  readonly labelFa: string;
  readonly missing: readonly string[];
}

interface ControlAccount {
  readonly role: string;
  readonly labelFa: string;
  readonly isConfigured: boolean;
  readonly accountCode: string | null;
  readonly accountName: string | null;
  readonly accountRef: string | null;
}

interface SetupStatusResponse {
  readonly isReady: boolean;
  readonly baseCurrency: string;
  readonly sections: {
    readonly fiscalYear: SetupSection;
    readonly books: SetupSection;
    readonly controlAccounts: SetupSection;
    readonly taxAndTerms: SetupSection;
    readonly numbering: SetupSection;
  };
  readonly controlAccounts: readonly ControlAccount[];
  readonly missingRequirements: readonly string[];
}

export function AccountingSetupCard({ base }: { readonly base: string }) {
  const setup = useApiGet<SetupStatusResponse>(`${base}/api/v1/finance/setup/status`, []);

  if (setup.status === 'loading') {
    return (
      <V2Card>
        <V2CardHeader
          icon="settings"
          title="پیکربندی حسابداری و حساب‌های کنترلی"
          subtitle="در حال بررسی آمادگی سیستم..."
        />
        <div style={{ padding: '16px', color: 'var(--text-muted)' }}>در حال بارگذاری...</div>
      </V2Card>
    );
  }

  if (setup.status === 'error') {
    return (
      <V2Card>
        <V2CardHeader
          icon="x"
          title="پیکربندی حسابداری و حساب‌های کنترلی"
          subtitle="خطا در دریافت وضعیت تنظیمات"
        />
        <div style={{ padding: '16px', color: 'var(--text-danger)' }}>
          اطلاعات وضعیت پیکربندی در دسترس نیست.
        </div>
      </V2Card>
    );
  }

  const data = setup.data;
  const isReady = data.isReady;

  return (
    <V2Card>
      <V2CardHeader
        icon="settings"
        title="وضعیت پیکربندی مالی و حساب‌های کنترلی"
        subtitle={`ارز پایه: ${data.baseCurrency} | وضعیت کلی: ${isReady ? 'آمادهٔ عملیات' : 'نیازمند تکمیل'}`}
        actions={
          <V2Badge tone={isReady ? 'success' : 'warn'}>{isReady ? 'آماده' : 'ناقص'}</V2Badge>
        }
      />
      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {data.missingRequirements.length > 0 && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: 'var(--surface-warning-subtle, #fff8e6)',
              borderRadius: '8px',
              border: '1px solid var(--border-warning, #f5c066)',
            }}
          >
            <div
              style={{
                fontWeight: 600,
                color: 'var(--text-warning, #b35900)',
                marginBottom: '6px',
              }}
            >
              موارد مسدودکنندهٔ عملیات مالی:
            </div>
            <ul style={{ margin: 0, paddingRight: '20px', fontSize: '13px', lineHeight: '1.6' }}>
              {data.missingRequirements.map((req, i) => (
                <li key={i}>{req}</li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
            حساب‌های کنترلی اصلی (سیستم دفترداری متمرکز):
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: '8px',
            }}
          >
            {data.controlAccounts.map((c) => (
              <div
                key={c.role}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  backgroundColor: 'var(--surface-subtle, #f9fafb)',
                  border: '1px solid var(--border-subtle, #e5e7eb)',
                  fontSize: '13px',
                }}
              >
                <div>
                  <div style={{ fontWeight: 500 }}>{c.labelFa}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted, #6b7280)' }}>
                    {c.isConfigured
                      ? `کد: ${c.accountCode ?? '—'} (${c.accountName ?? ''})`
                      : 'تعریف نشده'}
                  </div>
                </div>
                <span
                  style={{
                    color: c.isConfigured
                      ? 'var(--text-success, #10b981)'
                      : 'var(--text-danger, #ef4444)',
                    display: 'flex',
                    flexShrink: 0,
                  }}
                >
                  <Icon name={c.isConfigured ? 'check' : 'x'} size="sm" />
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </V2Card>
  );
}
